'use client';

import { Contact } from "@/app/lib/generated/graphql-types";
import ApproachMapContent from "@/app/features/sites/header/ui/desktop/flyout-info/flyouts/ui/approach/ApproachMapContent";
import FocusMap from "@/app/__to_be_integrated/_____components/map/FocusMap";
import { useEffect, useState } from "react";
import { LatLngLiteral } from "@googlemaps/google-maps-services-js";
import {
    getAddressCoordinates
} from "@/app/features/sites/header/ui/desktop/flyout-info/flyouts/data-access/queries";
import type { MapViewProps } from "@/app/__to_be_integrated/_____components/map/MapView";
import { Box, Typography } from "@mui/material";
import { ControlPosition } from "@vis.gl/react-google-maps";

export default function ContactMap({ contact, consentPosition }: { contact: Contact, consentPosition?: MapViewProps["consentPosition"]; }) {
    const addressParts = [contact?.title, contact?.address?.street, contact?.address?.zipCode, contact?.address?.city]
        .filter((part): part is string => typeof part === "string" && part.trim().length > 0);
    const address = addressParts.join(", ");
    const hasAddress = address.length > 0;
    const externalMapUrl = address
        ? `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(address)}`
        : undefined;

    const [coordinates, setCoordinates] = useState<LatLngLiteral>();
    const [hasLookupError, setHasLookupError] = useState(false);

    useEffect(() => {
        let isMounted = true;

        if (!hasAddress) return () => {
            isMounted = false;
        };

        getAddressCoordinates(address)
            .then((nextCoordinates) => {
                if (!isMounted) return;

                if (
                    nextCoordinates &&
                    Number.isFinite(nextCoordinates.lat) &&
                    Number.isFinite(nextCoordinates.lng)
                ) {
                    setHasLookupError(false);
                    setCoordinates(nextCoordinates);
                    return;
                }

                setHasLookupError(true);
            })
            .catch(() => {
                if (!isMounted) return;
                setHasLookupError(true);
            });

        return () => {
            isMounted = false;
        };
    }, [address, hasAddress]);

    const mapRegionLabel = address
        ? `Standortkarte: ${address}`
        : "Standortkarte";

    if (!hasAddress || hasLookupError) {
        return (
            <Box
                component="section"
                role="region"
                aria-label={mapRegionLabel}
                sx={{
                    width: "100%",
                    height: "100%",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    p: 3,
                }}
            >
                <Box sx={{ textAlign: "center", maxWidth: "28rem" }}>
                    <Typography variant="body1" sx={{ mb: 2 }}>
                        Die Karte konnte nicht geladen werden.
                    </Typography>
                    {externalMapUrl && (
                        <Box
                            component="a"
                            href={externalMapUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                        >
                            Route in Google Maps öffnen
                        </Box>
                    )}
                </Box>
            </Box>
        );
    }

    if (coordinates === undefined) {
        return (
            <Box
                component="section"
                role="region"
                aria-label={mapRegionLabel}
                sx={{
                    width: "100%",
                    height: "100%",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    p: 3,
                }}
            >
                <Box role="status" aria-live="polite">
                    <Typography variant="body1">Karte wird geladen ...</Typography>
                </Box>
            </Box>
        );
    }

    return (
        <Box
            component="section"
            role="region"
            aria-label={mapRegionLabel}
            sx={{ width: "100%", height: "100%", position: "relative" }}
        >
            <FocusMap
                mapId={process.env.NEXT_PUBLIC_MAPS_ID_APPROACH!}
                gestureHandling="none"
                zoomControl={true}
                fitPoints={[coordinates]}
                consentPosition={consentPosition}
                zoomControlOptions={{
                    position: ControlPosition.RIGHT_BOTTOM
                }}
            >
                <ApproachMapContent coordinates={coordinates} />
            </FocusMap>

            {externalMapUrl && (
                <Box
                    component="a"
                    href={externalMapUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    sx={{
                        position: "absolute",
                        left: 16,
                        bottom: 16,
                        px: 2,
                        py: 1,
                        borderRadius: 1,
                        bgcolor: "background.paper",
                        boxShadow: 2,
                        textDecoration: "none",
                        zIndex: 2,
                        "&:hover, &:focus-visible": {
                            textDecoration: "underline",
                        },
                    }}
                >
                    Route in Google Maps öffnen
                </Box>
            )}
        </Box>
    )
}
